import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { IOrganization, IUser } from "../../../model/model";
import axios from "axios";
import apiUrls from "../../../api";
import { storeAuthInLocalStorage } from "../../../common/util/localStorage";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import { ZendeskAPI } from "react-zendesk";
import { clearProfile } from "../../profile/state/profileSlice";
import type { IProjectGroupWithProjects } from "../../projectGroups/state/projectGroupsSlice";
import {
  clearProjectGroups,
  setProjectGroupsWithProjects,
} from "../../projectGroups/state/projectGroupsSlice";
import { clearProjects } from "../../graph/state/projectsSliceLegacy";
import { clearGallery } from "../../gallery/state/gallerySlice";
import { clearTeam } from "../../team/state/teamSlice";
import { clearAlerts } from "../../alert/state/alertsSlice";
import type { RootState } from "src/common/state/store";
import { destroyHelpDocsWidget } from "src/common/util/helpDocsLighthouse/destroyHelpDocsWidget";
import { clearDeviceInstallerState } from "../../deviceInstaller/state/deviceInstallerSlice";
import { clearBuilds } from "../../builds/state/buildsSlice";

interface ILegals {
  tos: { s3_url: string };
  privacyPolicy: { s3_url: string };
}

export interface IAuthFormData {
  email: string;
  password: string;
  from_environment?: string;
  rememberme: boolean;
}

export interface IForgottenPasswordFormData {
  email: string;
}

export interface IResetPasswordFormData {
  password: string;
  passwordConfirmation: string;
}

export interface ISetPasswordFormData {
  password: string;
  passwordConfirmation: string;
}

interface IAuth {
  jwt: string;
  helpDocsJwt: string;
  userId?: number;
  currentCommitHash: string;
  legals: ILegals;
}

const initialState: IAuth = {
  jwt: "",
  helpDocsJwt: "",
  currentCommitHash: "",
  legals: {
    tos: {
      s3_url: "",
    },
    privacyPolicy: {
      s3_url: "",
    },
  },
};

export const signIn = createAsyncThunk(
  "auth/login",
  async (
    { email, password, rememberme, from_environment = "WebApp" }: IAuthFormData & IAppLoaderAction,
    { dispatch },
  ) => {
    const response = await axios.post<{
      jwt: string;
      helpDocsJwt: string;
      user: IUser;
      firstTwoProjectGroupsWithProjects?: IProjectGroupWithProjects[];
    }>(
      `${apiUrls.auth.signIn}?populate=*&populate[organization][populate][admin]=*&populate[organization][populate][source_logo]=*`,
      {
        identifier: email,
        password,
        from_environment,
        remember_me: rememberme,
      },
    );

    const { jwt, helpDocsJwt, user, firstTwoProjectGroupsWithProjects } = response.data;
    const organization = user.organization;

    if (
      Array.isArray(firstTwoProjectGroupsWithProjects) &&
      firstTwoProjectGroupsWithProjects.length > 0
    ) {
      dispatch(setProjectGroupsWithProjects(firstTwoProjectGroupsWithProjects));
    }
    // Storing jwt and user in local storage
    await storeAuthInLocalStorage(user, organization, jwt, helpDocsJwt, true);

    return { jwt, helpDocsJwt };
  },
);

export const getEditorWebLastCommit = createAsyncThunk("auth/getEditorWebLastCommit", async () => {
  const response = await axios.get(apiUrls.getEditorWebLastCommit);
  return { legals: response.data };
});

export const fetchLegals = createAsyncThunk(
  "auth/fetchLegals",
  async (_: IAppLoaderAction, { rejectWithValue }) => {
    try {
      const response = await axios.get<ILegals>(apiUrls.legals);
      return { legals: response.data };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      }
      throw err;
    }
  },
);

export const forgottenPassword = createAsyncThunk(
  "auth/forgottenPassword",
  async ({ email }: IForgottenPasswordFormData & IAppLoaderAction, { rejectWithValue }) => {
    try {
      const response = await axios.post<{ jwt: string; user: IUser }>(apiUrls.auth.forgetPassword, {
        email,
      });
      // window.location.replace("/");
      return { response };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      } else {
        throw err;
      }
    }
  },
);
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (
    {
      password,
      passwordConfirmation,
      code,
    }: IResetPasswordFormData & { code: string } & IAppLoaderAction,
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post<{ jwt: string; user: IUser }>(apiUrls.auth.resetPassword, {
        code: code,
        password: password,
        passwordConfirmation: passwordConfirmation,
      });
      return { response };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      } else {
        throw err;
      }
    }
  },
);

export const setInitialPassword = createAsyncThunk(
  "auth/setInitialPassword",
  async (
    {
      password,
      passwordConfirmation,
      code,
    }: ISetPasswordFormData & { code: string } & IAppLoaderAction,
    { rejectWithValue },
  ) => {
    try {
      // CRITICAL: Remove Authorization header for this unauthenticated endpoint
      // User may be logged in when testing invite links, but this endpoint
      // authenticates via the resetPasswordToken in the request body, not JWT
      const response = await axios.post<{ jwt: string; user: IUser }>(
        `${apiUrls.base}/api/auth/set-initial-password`,
        {
          data: { code, password, passwordConfirmation },
        },
        {
          headers: {
            Authorization: undefined, // Explicitly remove the JWT header
          },
        },
      );
      return { response };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        // Map backend error messages to translation keys
        const errorMessage = err?.response?.data?.error?.message || "An error occurred";

        // Map known backend errors to translation keys
        const errorMap: { [key: string]: string } = {
          "Missing required fields": "pages.setPassword.errors.missingFields",
          "Passwords do not match": "pages.setPassword.errors.passwordsDoNotMatch",
          "Password requirements not met": "pages.setPassword.errors.passwordRequirementsNotMet",
          "Invalid or already used token": "pages.setPassword.errors.invalidOrUsedToken",
        };

        const translationKey = errorMap[errorMessage] || "pages.setPassword.errors.genericError";

        // Return translation key instead of raw error message
        return rejectWithValue({
          error: {
            message: translationKey,
          },
        });
      } else {
        throw err;
      }
    }
  },
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuth: (
      state: IAuth,
      action: PayloadAction<{ jwt: string; user: IUser; organization: IOrganization }>,
    ) => {
      state.jwt = action.payload.jwt;
    },
    resetJwt: (state: IAuth) => {
      state.jwt = "";
      axios.defaults.headers.common["Authorization"] = undefined;
      window.localStorage.clear();
    },
    setCurrentCommitHash: (state: IAuth, action: PayloadAction<string>) => {
      state.currentCommitHash = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signIn.fulfilled, (state, { payload }) => {
      state.jwt = payload.jwt;
      state.helpDocsJwt = payload.helpDocsJwt;
    });
    builder.addCase(fetchLegals.fulfilled, (state, { payload }) => {
      state.legals = payload.legals;
    });
  },
});

export const authReducer = authSlice.reducer;

export const getHelpDocsJwt = (state: RootState) => {
  return state.auth.helpDocsJwt;
};

export const getLegals = (state: IAuth) => state.legals;

export const logoutAuth = (): any => (dispatch: any) => {
  ZendeskAPI("webWidget", "hide");
  dispatch(clearProfile());
  dispatch(clearProjectGroups());
  dispatch(clearProjects());
  dispatch(clearGallery());
  dispatch(clearTeam());
  dispatch(clearAlerts());
  dispatch(clearDeviceInstallerState());
  dispatch(clearBuilds());
  // We don't want to discard localStorage.CURRENT_COMMIT_HASH
  const CURRENT_COMMIT_HASH = localStorage.getItem("CURRENT_COMMIT_HASH");
  dispatch(authSlice.actions.resetJwt());
  if (CURRENT_COMMIT_HASH) {
    localStorage.setItem("CURRENT_COMMIT_HASH", CURRENT_COMMIT_HASH);
  }

  destroyHelpDocsWidget();
};

export const { setAuth } = authSlice.actions;
